import { useMemo, useState } from 'react';
import { Button, TextInput, NumberInput } from '@tremor/react';
import type { AdminUser, PermissionsCatalog } from '../api/admin';
import type { Role } from '../api/types';
import { t } from '../lib/i18n';
import Modal from './Modal';

export interface UserFormValue {
  username: string;
  password?: string;
  role: Role;
  sid_assigned: number | null;
  permissions: string[];
  email: string; // '' = no remote access
}

interface Props {
  mode: 'create' | 'edit';
  initial?: AdminUser | null;
  catalog?: PermissionsCatalog | null;
  onSubmit: (v: UserFormValue) => Promise<void> | void;
  onCancel: () => void;
  errorText?: string | null;
}

// Bucket every catalog permission into a UI group so the editor stays
// scannable. Keys map to t.admin.permsGroups.
const PERM_GROUPS: { key: keyof typeof t.admin.permsGroups; perms: string[] }[] = [
  { key: 'areas', perms: [
    'view:overview', 'view:sales', 'view:parts', 'view:ar', 'view:inventory',
    'view:vendors', 'view:salespeople', 'view:lost',
  ]},
  { key: 'drilldown', perms: ['view:customer.detail', 'view:part.detail'] },
  { key: 'sensitive', perms: ['cost:visible', 'invoice:download'] },
  { key: 'archives',  perms: ['view:archive', 'archive:download'] },
  { key: 'features',  perms: ['reports:use'] },
  { key: 'admin',     perms: ['user:seed'] },
];

/**
 * Modal-ish form for creating or editing a user. Same component for both
 * modes — the difference is whether the username and password fields are
 * editable. Renders inside the shared Modal so callers get focus trap,
 * Escape/backdrop-close, and body scroll lock for free.
 */
export default function UserFormDialog({ mode, initial, catalog, onSubmit, onCancel, errorText }: Props) {
  const [username, setUsername] = useState(initial?.username ?? '');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState(initial?.email ?? '');
  const [role, setRole] = useState<Role>(initial?.role ?? 'manager');
  const [sid, setSid] = useState<number | null>(initial?.sid_assigned ?? null);
  const [overrides, setOverrides] = useState<Set<string>>(
    () => new Set(initial?.permissions ?? []),
  );
  const [busy, setBusy] = useState(false);
  const [localErr, setLocalErr] = useState<string | null>(null);
  const [showPerms, setShowPerms] = useState(false);

  // Role defaults are locked-on in the UI; overrides are toggleable.
  const roleDefaults = useMemo(() => {
    return new Set(catalog?.defaults_by_role[role] ?? []);
  }, [catalog, role]);

  function togglePerm(p: string) {
    if (roleDefaults.has(p)) return; // can't toggle a role default
    setOverrides((prev) => {
      const next = new Set(prev);
      if (next.has(p)) next.delete(p); else next.add(p);
      return next;
    });
  }

  const isStaff = role === 'staff';
  const titleText = mode === 'create' ? t.admin.dialog.titleCreate : t.admin.dialog.titleEdit;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLocalErr(null);
    if (mode === 'create' && password.length < 8) {
      setLocalErr(t.admin.errors.passwordShort);
      return;
    }
    if (isStaff && (sid === null || sid <= 0)) {
      setLocalErr(t.admin.dialog.sidHint);
      return;
    }
    setBusy(true);
    try {
      // Only send overrides — perms that aren't role-defaults. The backend
      // resolves Has() as (role_defaults ∪ overrides), so we don't echo
      // role defaults back. Saves storage + makes audit logs clearer.
      const perms = [...overrides].filter((p) => !roleDefaults.has(p));
      await onSubmit({
        username: username.trim(),
        password: password.length > 0 ? password : undefined,
        role,
        sid_assigned: isStaff ? sid : null,
        permissions: perms,
        email: email.trim(),
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal onClose={onCancel} ariaLabel={titleText} panelClassName="max-w-md p-5">
      <form onSubmit={handleSubmit} className="space-y-4">
        <h2 className="text-lg font-semibold text-tremor-content-strong">{titleText}</h2>

        <label className="block text-sm">
          <span className="text-tremor-content text-xs mb-1 block">{t.admin.dialog.username}</span>
          <TextInput
            value={username}
            onValueChange={setUsername}
            disabled={mode === 'edit'}
            required
          />
        </label>

        <label className="block text-sm">
          <span className="text-tremor-content text-xs mb-1 block">{t.admin.dialog.password}</span>
          <TextInput
            type="password"
            value={password}
            onValueChange={setPassword}
            placeholder={mode === 'create' ? t.admin.dialog.passwordPlaceholder : t.admin.dialog.passwordNewOnly}
            required={mode === 'create'}
          />
        </label>

        <label className="block text-sm">
          <span className="text-tremor-content text-xs mb-1 block">{t.admin.dialog.email}</span>
          <TextInput type="email" value={email} onValueChange={setEmail} placeholder={t.admin.dialog.emailPlaceholder} />
          <span className="text-tremor-content text-xs mt-1 block">{t.admin.dialog.emailHint}</span>
        </label>

        <label className="block text-sm">
          <span className="text-tremor-content text-xs mb-1 block">{t.admin.dialog.role}</span>
          {/* Native <select> because Tremor's Select uses a Listbox popover
              that stacks under the modal backdrop's z-40. The browser-managed
              dropdown always renders above. */}
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as Role)}
            className="w-full rounded-tremor-default border border-tremor-border bg-tremor-background py-2 px-3 text-sm text-tremor-content-strong focus:border-tremor-brand focus:outline-none focus:ring-2 focus:ring-tremor-brand-muted"
          >
            <option value="owner">{t.roles.owner}</option>
            <option value="manager">{t.roles.manager}</option>
            <option value="staff">{t.roles.staff}</option>
            <option value="accounting">{t.roles.accounting}</option>
          </select>
        </label>

        {isStaff && (
          <label className="block text-sm">
            <span className="text-tremor-content text-xs mb-1 block">{t.admin.dialog.sid}</span>
            <NumberInput
              min={1}
              value={sid ?? 0}
              onValueChange={(n) => setSid(typeof n === 'number' && n > 0 ? n : null)}
            />
            <span className="text-xs text-tremor-content block mt-1">{t.admin.dialog.sidHint}</span>
          </label>
        )}

        {catalog && (
          <div className="border-t border-tremor-border pt-3 space-y-2">
            <button
              type="button"
              onClick={() => setShowPerms((v) => !v)}
              className="text-sm font-semibold text-tremor-content-strong flex items-center gap-1"
            >
              <span>{showPerms ? '▾' : '▸'}</span>
              {t.admin.permsSection}
            </button>
            {showPerms && (
              <div className="space-y-3">
                <div className="text-xs text-tremor-content">{t.admin.permsHint}</div>
                {PERM_GROUPS.map((g) => {
                  const groupPerms = g.perms.filter((p) => catalog.permissions.includes(p));
                  if (groupPerms.length === 0) return null;
                  return (
                    <div key={g.key}>
                      <div className="text-tremor-content text-xs font-semibold mb-1">
                        {t.admin.permsGroups[g.key]}
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1">
                        {groupPerms.map((p) => {
                          const locked = roleDefaults.has(p);
                          const checked = locked || overrides.has(p);
                          const label = (t.admin.permsLabels as Record<string, string>)[p] ?? p;
                          return (
                            <label
                              key={p}
                              className={
                                'flex items-center gap-2 text-sm cursor-pointer ' +
                                (locked ? 'text-tremor-content' : 'text-tremor-content-strong')
                              }
                              title={locked ? t.admin.permsRoleLocked : undefined}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                disabled={locked}
                                onChange={() => togglePerm(p)}
                              />
                              <span>{label}</span>
                              {locked && (
                                <span className="text-xs text-tremor-content">·{t.admin.permsRoleLocked}</span>
                              )}
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {(localErr || errorText) && (
          <div className="text-sm text-red-700 bg-red-50 border border-red-200 dark:text-red-300 dark:bg-red-950 dark:border-red-900 rounded px-3 py-2">
            {localErr ?? errorText}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="light" onClick={onCancel} disabled={busy}>
            {t.admin.dialog.cancel}
          </Button>
          <Button type="submit" loading={busy}>
            {t.admin.dialog.save}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
